import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { TAppointment } from '../types/appointment';
import { get_current_datetime, formatDate } from '../utils/datetime';
import {
  getAppointments,
  getBookedAppointments,
  fetchTodaysPatients
} from '../services/apicalls/appointment';
import { set } from 'zod';

export const fetchTodaysBookedAppointments = createAsyncThunk(
  'appoinments/todaysbookedappointments',
  async (doctor_id: number = 0) => {
    let today = get_current_datetime(true);
    let queryString = `fromdate=${today}&todate=${today}`;
    if (doctor_id) {
      queryString.concat(`&doctor_id=${doctor_id}`);
    }
    try {
      const { data } = await getBookedAppointments(queryString);
      return data;
    } catch (err) {
      return [];
    }
  }
);

export const fetchTodaysPatientToConsultated = createAsyncThunk(
  'appoinments/todaysPatientToConsultated',
  async (postData: {
    doctor_id: number;
  }) => {
    try {
      const { data } = await fetchTodaysPatients(postData.doctor_id);
      //@ts-ignore
      return { doctor_id: postData.doctor_id, consult_dates: data.consult_dates, appointments: data.appointments }
    } catch (err) {
      return { doctor_id: postData.doctor_id, consult_dates: [], appointments: {} };
    }
  }
);

type TAppointments = {
  todaysBookedAppointments: TAppointment[];
  todaysPatientToConsultated: Record<number, { consult_dates: string[], appointments: Record<string, TAppointment[]> }>;
  newAppointmentBooked: boolean;
  loading: boolean;
  todaysPatinetLoading: boolean;
  todaysBookingSummary: Record<string, {
    today_bookings: number;
    today_online_bookings: number;
  }>|null;
  patientsSummary:Record<string,{
    total_patients: number;
    online_booked_patients: number;
  }>|null;
};

const initialState: TAppointments = {
  todaysBookedAppointments: [],
  todaysPatientToConsultated: {},
  newAppointmentBooked: false,
  loading: true,
  todaysPatinetLoading: true,
  todaysBookingSummary: null,
  patientsSummary:null
};

const appointments = createSlice({
  name: 'appoinments',
  initialState: initialState,
  reducers: {
    setUpcomingAppointments: (state, action: PayloadAction<{ doctor_id: number, appointments: Record<string, TAppointment[]>, consult_dates: string[] }>) => {
      state.todaysPatientToConsultated[action.payload.doctor_id] = {
        appointments: action.payload.appointments,
        consult_dates: action.payload.consult_dates
      }
    },
    updateBookingPayment: (state, action: PayloadAction<{ booking_id: number, doctor_id: number, consult_date: string, payment_status: string, booking_amount: number, total_amount: number,patient_paid_amount:string }>) => {
      let consult_date = formatDate(action.payload.consult_date);
      if (state.todaysPatientToConsultated[action.payload.doctor_id]) {
        state.todaysPatientToConsultated[action.payload.doctor_id].appointments[consult_date] = (state.todaysPatientToConsultated[action.payload.doctor_id].appointments[consult_date] || []).map((appointment) => {
          if (appointment.id == action.payload.booking_id) {
            return { ...appointment, payment_status: action.payload.payment_status, total_amount: action.payload.total_amount, booking_charge: action.payload.booking_amount,patient_paid_amount:action.payload.patient_paid_amount }
          }
          return appointment
        })
      }
    },
    updateBookingStatus: (state, action: PayloadAction<{ booking_id: number, doctor_id: number, consult_date: string, status: string }>) => {
      let consult_date = formatDate(action.payload.consult_date);
      if (state.todaysPatientToConsultated[action.payload.doctor_id]) {
        state.todaysPatientToConsultated[action.payload.doctor_id].appointments[consult_date] = (state.todaysPatientToConsultated[action.payload.doctor_id].appointments[consult_date] || []).map((appointment) => {
          if (appointment.id == action.payload.booking_id) {
            return { ...appointment, status: action.payload.status }
          }
          return appointment
        })
      }
    },
    AnnounceAppointmentBooked(state) {
      state.newAppointmentBooked = true;
    },
    resetNewAppointmentBookingState(state) {
      state.newAppointmentBooked = false;
    },
    updateTodaysBookingSummary(state, action:PayloadAction<Record<string, {
      today_bookings: number;
      today_online_bookings: number;
    }>>) {
      state.todaysBookingSummary = action.payload;
    },
    updatePatientsSummary(state, action:PayloadAction<Record<string,{
      total_patients: number;
      online_booked_patients: number;
    }>>) {
      state.patientsSummary = action.payload;
    },
    updateBookingTags: (state, action: PayloadAction<{ booking_id: number, doctor_id: number, consult_date: string, tags: string }>) => {
      const consult_date = formatDate(action.payload.consult_date);
      if (state.todaysPatientToConsultated[action.payload.doctor_id]) {
        state.todaysPatientToConsultated[action.payload.doctor_id].appointments[consult_date] = (state.todaysPatientToConsultated[action.payload.doctor_id].appointments[consult_date] || []).map((appointment) => {
          if (appointment.id === action.payload.booking_id) {
            return { ...appointment, tags: action.payload.tags };
          }
          return appointment;
        });
      }
    },
    updateBookingPatientInfo: (state, action: PayloadAction<{ booking_id: number, doctor_id: number, consult_date: string, patient_name: string, patient_age: string, patient_address: string }>) => {
      const consult_date = formatDate(action.payload.consult_date);
      if (state.todaysPatientToConsultated[action.payload.doctor_id]) {
        state.todaysPatientToConsultated[action.payload.doctor_id].appointments[consult_date] = (state.todaysPatientToConsultated[action.payload.doctor_id].appointments[consult_date] || []).map((appointment) => {
          if (appointment.id === action.payload.booking_id) {
            return {
              ...appointment,
              patient_name: action.payload.patient_name,
              patient_age: action.payload.patient_age,
              patient_address: action.payload.patient_address,
            };
          }
          return appointment;
        });
      }
    },
  },
  extraReducers(builder) {
    builder.addCase(
      fetchTodaysBookedAppointments.fulfilled,
      (state, action: PayloadAction<TAppointment[]>) => {
        state.todaysBookedAppointments = action.payload;
        state.loading = false;
      }
    );
    builder.addCase(
      fetchTodaysPatientToConsultated.fulfilled,
      (state, action: PayloadAction<{ doctor_id: number, consult_dates: string[], appointments: Record<string, TAppointment[]> }>) => {
        state.todaysPatientToConsultated[action.payload.doctor_id] = { consult_dates: action.payload.consult_dates, appointments: action.payload.appointments };
        state.todaysPatinetLoading = false;
      }
    );
  },
});

export const { setUpcomingAppointments, AnnounceAppointmentBooked, resetNewAppointmentBookingState, updateBookingPayment, updateBookingStatus, updateTodaysBookingSummary, updatePatientsSummary, updateBookingTags, updateBookingPatientInfo } =
  appointments.actions;

export default appointments.reducer;
